import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Alert, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import * as FaIcons from 'react-icons/fa'
import PropTypes from 'prop-types'
import 'react-datepicker/dist/react-datepicker.css'

// Registrar locale español
registerLocale('es', es)

const SendEmailModal = ({ show, onHide, onSendEmails, cierreDetalle, loading, sendingEmails, emailTemplates, loadingEmailTemplates }) => {
    // Estados del formulario
    const [fechaAceptacionFactura, setFechaAceptacionFactura] = useState('')
    const [fechaPago, setFechaPago] = useState('')
    const [fechasNoPago, setFechasNoPago] = useState([])
    const [fechaNoPagoTemp, setFechaNoPagoTemp] = useState('')
    const [envioTest, setEnvioTest] = useState(false)
    const [correoTest, setCorreoTest] = useState('')
    const [mensajeTardio, setMensajeTardio] = useState(false)
    const [conductoresExcluir, setConductoresExcluir] = useState([])
    const [empresasExcluir, setEmpresasExcluir] = useState([])
    const [tmsExcluir, setTmsExcluir] = useState([])

    // Estado para template seleccionado
    const [templateSlug, setTemplateSlug] = useState('cierre_definitivo')
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [camposConfigValues, setCamposConfigValues] = useState({})
    const [fechasListaTemp, setFechasListaTemp] = useState({})
    // Estados de validación
    const [errors, setErrors] = useState({})
    
    // Estados para búsqueda y filtrado
    const [searchConductor, setSearchConductor] = useState('')
    const [searchEmpresa, setSearchEmpresa] = useState('')
    const [searchTms, setSearchTms] = useState('')

    // Limpiar formulario cuando se cierra el modal
    useEffect(() => {
        if (!show) {
            setFechaAceptacionFactura('')
            setFechaPago('')
            setFechasNoPago([])
            setFechaNoPagoTemp('')
            setEnvioTest(false)
            setCorreoTest('')
            setMensajeTardio(false)
            setConductoresExcluir([])
            setEmpresasExcluir([])
            setTmsExcluir([])
            setErrors({})
            setSearchConductor('')
            setSearchEmpresa('')
            setSearchTms('')
            setTemplateSlug('cierre_definitivo')
            setSelectedTemplate(null)
            setCamposConfigValues({})
            setFechasListaTemp({})
        }
    }, [show])

    // Sincronizar selectedTemplate cuando cambia templateSlug o emailTemplates
    useEffect(() => {
        if (emailTemplates && emailTemplates.length > 0) {
            const found = emailTemplates.find(t => t.slug === templateSlug) || null
            setSelectedTemplate(found)
            // Reiniciar valores de campos dinámicos al cambiar template
            setCamposConfigValues({})
            setFechasListaTemp({})
        }
    }, [templateSlug, emailTemplates])

    // Filtrar y ordenar conductores
    const getConductoresFiltrados = () => {
        if (!cierreDetalle?.usuarios_moviles) return []
        
        return cierreDetalle.usuarios_moviles
            .filter(conductor => 
                conductor.usuarioSpread.toLowerCase().includes(searchConductor.toLowerCase())
            )
            .sort((a, b) => a.usuarioSpread.localeCompare(b.usuarioSpread))
    }

    // Filtrar y ordenar empresas
    const getEmpresasFiltradas = () => {
        if (!cierreDetalle?.empresas) return []
        
        return cierreDetalle.empresas
            .filter(empresa => 
                empresa.sigla.toLowerCase().includes(searchEmpresa.toLowerCase())
            )
            .sort((a, b) => a.sigla.localeCompare(b.sigla))
    }

    // Filtrar y ordenar TMS
    const getTmsFiltrados = () => {
        if (!cierreDetalle?.tms) return []
        
        return cierreDetalle.tms
            .filter(tms => 
                tms.tms.toLowerCase().includes(searchTms.toLowerCase())
            )
            .sort((a, b) => a.tms.localeCompare(b.tms))
    }

    // Validar formulario
    const validateForm = () => {
        const newErrors = {}
        const isDefault = templateSlug === 'cierre_definitivo'

        if (isDefault) {
            if (!fechaAceptacionFactura) {
                newErrors.fechaAceptacionFactura = 'La fecha límite de aceptación de facturas es obligatoria'
            }
            if (!fechaPago) {
                newErrors.fechaPago = 'La fecha de pago es obligatoria'
            }
            if (fechasNoPago.length === 0) {
                newErrors.fechasNoPago = 'Debe agregar al menos una fecha de no pago'
            }
        } else if (selectedTemplate?.campos_config) {
            // Validar campos requeridos del template dinámico
            let camposConfig = []
            try {
                camposConfig = typeof selectedTemplate.campos_config === 'string'
                    ? JSON.parse(selectedTemplate.campos_config)
                    : selectedTemplate.campos_config
            } catch (e) { /* ignore parse errors */ }
            camposConfig.forEach(campo => {
                if (campo.requerido) {
                    const val = camposConfigValues[campo.campo]
                    if (campo.tipo === 'fecha_lista') {
                        if (!val || val.length === 0) {
                            newErrors[`campo_${campo.campo}`] = `${campo.label} es obligatorio`
                        }
                    } else if (val === undefined || val === null || val === '') {
                        newErrors[`campo_${campo.campo}`] = `${campo.label} es obligatorio`
                    }
                }
            })
        }

        if (envioTest && !correoTest) {
            newErrors.correoTest = 'Debe ingresar un correo para el envío de prueba'
        }

        if (envioTest && correoTest && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correoTest)) {
            newErrors.correoTest = 'Debe ingresar un correo válido'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    // Agregar fecha de no pago
    const handleAddFechaNoPago = () => {
        if (fechaNoPagoTemp && !fechasNoPago.includes(fechaNoPagoTemp)) {
            setFechasNoPago([...fechasNoPago, fechaNoPagoTemp])
            setFechaNoPagoTemp('')
        }
    }

    // Eliminar fecha de no pago
    const handleRemoveFechaNoPago = fecha => {
        setFechasNoPago(fechasNoPago.filter(f => f !== fecha))
    }

    // Manejar envío del formulario
    const handleSubmit = e => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        const isDefault = templateSlug === 'cierre_definitivo'

        // Preparar datos para envío
        const emailData = {
            template_slug: templateSlug,
            conductores_id_excluir: conductoresExcluir,
            empresas_id_excluir: empresasExcluir,
            tms_id_excluir: tmsExcluir
        }

        if (isDefault) {
            emailData.fecha_aceptacion_factura = fechaAceptacionFactura
            emailData.fecha_pago = fechaPago
            emailData.fecha_no_pago = fechasNoPago
            emailData.mensaje_tardio = mensajeTardio
        } else {
            // Campos dinámicos del template seleccionado
            Object.assign(emailData, camposConfigValues)
        }

        // Agregar campos condicionales
        if (envioTest) {
            emailData.test = true
            emailData.correo_test = correoTest
        }

        onSendEmails(emailData)
    }

    // Formatear lista de exclusiones para mostrar en el resumen
    const getExclusionSummary = () => {
        const conductoresNames = conductoresExcluir.map(id => {
            const conductor = cierreDetalle?.usuarios_moviles?.find(u => u.usuario_movil_id === id)
            return conductor?.usuarioSpread || `ID: ${id}`
        })

        const empresasNames = empresasExcluir.map(id => {
            const empresa = cierreDetalle?.empresas?.find(e => e.empresa_id === id)
            return empresa?.sigla || `ID: ${id}`
        })

        const tmsNames = tmsExcluir.map(id => {
            const tms = cierreDetalle?.tms?.find(t => t.id === id)
            return tms?.tms || `ID: ${id}`
        })

        return { conductoresNames, empresasNames, tmsNames }
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            backdrop={sendingEmails ? 'static' : true}
            keyboard={!sendingEmails}>
            <Modal.Header closeButton={!sendingEmails}>
                <Modal.Title>
                    <FaIcons.FaEnvelope className="me-2" />
                    Enviar Correos del Cierre
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Información del cierre */}
                {cierreDetalle && (
                    <Alert variant="info" className="mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <strong>{cierreDetalle.cierre}</strong>
                                <br />
                                <small>
                                    Periodo: {cierreDetalle.fecha_inicio} - {cierreDetalle.fecha_termino}
                                    <br />
                                    Total de rutas: {cierreDetalle.estadisticas?.total_rutas || 0}
                                </small>
                            </div>
                            <div>
                                Estado: <br />
                                <Badge bg="primary" className="p-2">
                                    {cierreDetalle.estado_actual?.estado || 'Sin estado'}
                                </Badge>
                            </div>
                        </div>
                    </Alert>
                )}

                {/* Banner de modo test */}
                {envioTest && (
                    <Alert variant="warning" className="mb-4">
                        <FaIcons.FaExclamationTriangle className="me-2" />
                        <strong>Modo test activado:</strong> Se enviarán correos de 2 conductores y 2 empresas.
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                    {/* Selector de template */}
                    <div className="mb-4 p-3 border rounded bg-light">
                        <Form.Label className="fw-bold">
                            <FaIcons.FaFileAlt className="me-2 text-primary" />
                            Template de correo
                        </Form.Label>
                        {loadingEmailTemplates ? (
                            <div className="text-muted small">
                                <div className="spinner-border spinner-border-sm me-2" role="status" />
                                Cargando templates...
                            </div>
                        ) : (
                            <Form.Select
                                value={templateSlug}
                                onChange={e => setTemplateSlug(e.target.value)}
                                disabled={sendingEmails}
                            >
                                {emailTemplates && emailTemplates.length > 0
                                    ? emailTemplates.map(t => (
                                        <option key={t.id} value={t.slug}>{t.nombre}</option>
                                    ))
                                    : <option value="cierre_definitivo">Cierre Definitivo (por defecto)</option>
                                }
                            </Form.Select>
                        )}
                        {selectedTemplate?.descripcion && (
                            <Form.Text className="text-muted">{selectedTemplate.descripcion}</Form.Text>
                        )}
                    </div>

                    {/* Campos del template cierre_definitivo (existentes) */}
                    {templateSlug === 'cierre_definitivo' && (
                    <>
                    <Row>
                        {/* Fecha límite aceptación facturas */}
                        <Col md={6} className="mb-3">
                            <Form.Label>Fecha límite aceptación de facturas *</Form.Label>
                            <DatePicker
                                selected={fechaAceptacionFactura ? new Date(fechaAceptacionFactura + 'T00:00:00') : null}
                                onChange={date => {
                                    if (date) {
                                        const year = date.getFullYear();
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const day = String(date.getDate()).padStart(2, '0');
                                        setFechaAceptacionFactura(`${year}-${month}-${day}`);
                                    } else {
                                        setFechaAceptacionFactura('');
                                    }
                                }}
                                dateFormat="dd/MM/yyyy"
                                locale="es"
                                placeholderText="Seleccionar fecha"
                                className={`form-control ${errors.fechaAceptacionFactura ? 'is-invalid' : ''}`}
                            />
                            {errors.fechaAceptacionFactura && (
                                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                    {errors.fechaAceptacionFactura}
                                </Form.Control.Feedback>
                            )}
                        </Col>

                        {/* Fecha de pago */}
                        <Col md={6} className="mb-3">
                            <Form.Label>Fecha de pago *</Form.Label>
                            <DatePicker
                                selected={fechaPago ? new Date(fechaPago + 'T00:00:00') : null}
                                onChange={date => {
                                    if (date) {
                                        const year = date.getFullYear();
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const day = String(date.getDate()).padStart(2, '0');
                                        setFechaPago(`${year}-${month}-${day}`);
                                    } else {
                                        setFechaPago('');
                                    }
                                }}
                                dateFormat="dd/MM/yyyy"
                                locale="es"
                                placeholderText="Seleccionar fecha"
                                className={`form-control ${errors.fechaPago ? 'is-invalid' : ''}`}
                            />
                            {errors.fechaPago && (
                                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                    {errors.fechaPago}
                                </Form.Control.Feedback>
                            )}
                        </Col>
                    </Row>

                    {/* Fechas de no pago */}
                    <div className="mb-3">
                        <Form.Label>Fechas de no pago *</Form.Label>
                        <Row>
                            <Col md={8}>
                                <DatePicker
                                    selected={fechaNoPagoTemp ? new Date(fechaNoPagoTemp + 'T00:00:00') : null}
                                    onChange={date => {
                                        if (date) {
                                            const year = date.getFullYear();
                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                            const day = String(date.getDate()).padStart(2, '0');
                                            setFechaNoPagoTemp(`${year}-${month}-${day}`);
                                        } else {
                                            setFechaNoPagoTemp('');
                                        }
                                    }}
                                    dateFormat="dd/MM/yyyy"
                                    locale="es"
                                    placeholderText="Seleccionar fecha de no pago"
                                    className="form-control"
                                />
                            </Col>
                            <Col md={4}>
                                <Button
                                    variant="outline-primary"
                                    onClick={handleAddFechaNoPago}
                                    disabled={!fechaNoPagoTemp}
                                    className="w-100">
                                    <FaIcons.FaPlus className="me-1" />
                                    Agregar
                                </Button>
                            </Col>
                        </Row>

                        {/* Lista de fechas de no pago */}
                        {fechasNoPago.length > 0 && (
                            <div className="mt-2">
                                <small className="text-muted">Fechas agregadas de NO pago:</small>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    {fechasNoPago.map((fecha, index) => (
                                        <Badge key={index} bg="secondary" className="d-flex align-items-center gap-1">
                                            {fecha}
                                            <FaIcons.FaTimes
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleRemoveFechaNoPago(fecha)}
                                            />
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {errors.fechasNoPago && (
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {errors.fechasNoPago}
                            </Form.Control.Feedback>
                        )}
                    </div>
                    </> )} {/* fin cierre_definitivo */}

                    {/* Envío tardío (solo cierre_definitivo) */}
                    {templateSlug === 'cierre_definitivo' && (
                    <div className="mb-3">
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id="tooltip-envio-tardio-def" style={{ zIndex: 9999 }}>
                                    Si este checkbox se selecciona se agregará el mensaje de envío tardío del cierre
                                </Tooltip>
                            }
                            trigger={['hover', 'focus']}>
                            <Form.Check
                                type="checkbox"
                                id="mensaje-tardio"
                                label="Envío Tardío"
                                checked={mensajeTardio}
                                onChange={e => setMensajeTardio(e.target.checked)}
                            />
                        </OverlayTrigger>
                    </div>
                    )}

                    {/* Envío de prueba (siempre visible) */}
                    <div className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="envio-test"
                            label="Envío de prueba"
                            checked={envioTest}
                            onChange={e => setEnvioTest(e.target.checked)}
                        />

                        {envioTest && (
                            <div className="mt-2">
                                <Form.Label>Email para envío de test</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={correoTest}
                                    onChange={e => setCorreoTest(e.target.value)}
                                    placeholder="ejemplo@correo.com"
                                    isInvalid={!!errors.correoTest}
                                />
                                {errors.correoTest && (
                                    <Form.Control.Feedback type="invalid">{errors.correoTest}</Form.Control.Feedback>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Campos dinámicos para templates distintos de cierre_definitivo */}
                    {templateSlug !== 'cierre_definitivo' && selectedTemplate?.campos_config && (() => {
                        let camposConfig = []
                        try {
                            camposConfig = typeof selectedTemplate.campos_config === 'string'
                                ? JSON.parse(selectedTemplate.campos_config)
                                : selectedTemplate.campos_config
                        } catch (e) { return null }

                        return camposConfig.map(campo => {
                            const fieldError = errors[`campo_${campo.campo}`]
                            const value = camposConfigValues[campo.campo]

                            const handleChange = (val) => {
                                setCamposConfigValues(prev => ({ ...prev, [campo.campo]: val }))
                            }

                            return (
                                <div key={campo.campo} className="mb-3">
                                    <Form.Label>
                                        {campo.label}
                                        {campo.requerido && ' *'}
                                    </Form.Label>

                                    {campo.tipo === 'fecha' && (
                                        <DatePicker
                                            selected={value ? new Date(value + 'T00:00:00') : null}
                                            onChange={date => {
                                                if (date) {
                                                    const y = date.getFullYear()
                                                    const m = String(date.getMonth() + 1).padStart(2, '0')
                                                    const d = String(date.getDate()).padStart(2, '0')
                                                    handleChange(`${y}-${m}-${d}`)
                                                } else {
                                                    handleChange('')
                                                }
                                            }}
                                            dateFormat="dd/MM/yyyy"
                                            locale="es"
                                            placeholderText={campo.ayuda || 'Seleccionar fecha'}
                                            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                        />
                                    )}

                                    {campo.tipo === 'fecha_lista' && (
                                        <>
                                            <Row>
                                                <Col md={8}>
                                                    <DatePicker
                                                        selected={fechasListaTemp[campo.campo] ? new Date(fechasListaTemp[campo.campo] + 'T00:00:00') : null}
                                                        onChange={date => {
                                                            if (date) {
                                                                const y = date.getFullYear()
                                                                const m = String(date.getMonth() + 1).padStart(2, '0')
                                                                const d = String(date.getDate()).padStart(2, '0')
                                                                setFechasListaTemp(prev => ({ ...prev, [campo.campo]: `${y}-${m}-${d}` }))
                                                            }
                                                        }}
                                                        dateFormat="dd/MM/yyyy"
                                                        locale="es"
                                                        placeholderText={campo.ayuda || 'Seleccionar fecha'}
                                                        className="form-control"
                                                    />
                                                </Col>
                                                <Col md={4}>
                                                    <Button
                                                        variant="outline-primary"
                                                        className="w-100"
                                                        onClick={() => {
                                                            const temp = fechasListaTemp[campo.campo]
                                                            if (temp) {
                                                                const current = value || []
                                                                if (!current.includes(temp)) {
                                                                    handleChange([...current, temp])
                                                                }
                                                                setFechasListaTemp(prev => ({ ...prev, [campo.campo]: '' }))
                                                            }
                                                        }}
                                                        disabled={!fechasListaTemp[campo.campo]}
                                                    >
                                                        <FaIcons.FaPlus className="me-1" />
                                                        Agregar
                                                    </Button>
                                                </Col>
                                            </Row>
                                            {value && value.length > 0 && (
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    {value.map((f, i) => (
                                                        <Badge key={i} bg="secondary" className="d-flex align-items-center gap-1">
                                                            {f}
                                                            <FaIcons.FaTimes
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => handleChange(value.filter(x => x !== f))}
                                                            />
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {campo.tipo === 'checkbox' && (
                                        <Form.Check
                                            type="checkbox"
                                            id={`campo-${campo.campo}`}
                                            label={campo.ayuda || campo.label}
                                            checked={!!value}
                                            onChange={e => handleChange(e.target.checked)}
                                        />
                                    )}

                                    {campo.tipo === 'texto' && (
                                        <Form.Control
                                            type="text"
                                            value={value || ''}
                                            onChange={e => handleChange(e.target.value)}
                                            placeholder={campo.ayuda || ''}
                                            isInvalid={!!fieldError}
                                        />
                                    )}

                                    {campo.tipo === 'numero' && (
                                        <Form.Control
                                            type="number"
                                            value={value ?? ''}
                                            onChange={e => handleChange(e.target.value)}
                                            placeholder={campo.ayuda || ''}
                                            isInvalid={!!fieldError}
                                        />
                                    )}

                                    {campo.tipo === 'select' && (
                                        <Form.Select
                                            value={value || ''}
                                            onChange={e => handleChange(e.target.value)}
                                            isInvalid={!!fieldError}
                                        >
                                            <option value="">Seleccionar...</option>
                                            {(campo.opciones || []).map(opt => (
                                                <option key={opt.value || opt} value={opt.value || opt}>
                                                    {opt.label || opt}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    )}

                                    {fieldError && (
                                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                            {fieldError}
                                        </Form.Control.Feedback>
                                    )}
                                    {campo.ayuda && campo.tipo !== 'checkbox' && (
                                        <Form.Text className="text-muted">{campo.ayuda}</Form.Text>
                                    )}
                                </div>
                            )
                        })
                    })()}

                    {/* Envíos a excluir */}
                    <div className="mb-3">
                        <Form.Label>Envíos a excluir</Form.Label>
                        <Alert variant="warning" className="small">
                            <FaIcons.FaExclamationTriangle className="me-1" />
                            Los conductores, empresas o TMS seleccionados no recibirán el correo de cierre de conductores.
                        </Alert>

                        <Row>
                            {/* Conductores a excluir */}
                            <Col md={4}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="small mb-0">Conductores a excluir</Form.Label>
                                    <div className="d-flex gap-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => {
                                                const allConductorIds = getConductoresFiltrados().map(c => c.usuario_movil_id);
                                                setConductoresExcluir([...new Set([...conductoresExcluir, ...allConductorIds])]);
                                            }}
                                            disabled={getConductoresFiltrados().length === 0}
                                            title="Seleccionar todos los conductores visibles"
                                        >
                                            Todos
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setConductoresExcluir([])}
                                            disabled={conductoresExcluir.length === 0}
                                        >
                                            Ninguno
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Buscador de conductores */}
                                <div className="mb-2">
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar conductor..."
                                        value={searchConductor}
                                        onChange={(e) => setSearchConductor(e.target.value)}
                                        size="sm"
                                    />
                                </div>
                                
                                <div 
                                    className="border rounded p-2" 
                                    style={{ 
                                        maxHeight: '200px', 
                                        overflowY: 'auto',
                                        backgroundColor: '#f8f9fa'
                                    }}
                                >
                                    {getConductoresFiltrados().length > 0 ? (
                                        getConductoresFiltrados().map(conductor => (
                                            <Form.Check
                                                key={conductor.usuario_movil_id}
                                                type="checkbox"
                                                id={`conductor-${conductor.usuario_movil_id}`}
                                                label={conductor.usuarioSpread}
                                                checked={conductoresExcluir.includes(conductor.usuario_movil_id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setConductoresExcluir([...conductoresExcluir, conductor.usuario_movil_id]);
                                                    } else {
                                                        setConductoresExcluir(conductoresExcluir.filter(id => id !== conductor.usuario_movil_id));
                                                    }
                                                }}
                                                className="mb-1"
                                            />
                                        ))
                                    ) : (
                                        <small className="text-muted">
                                            {searchConductor ? 'No se encontraron conductores con ese término' : 'No hay conductores disponibles'}
                                        </small>
                                    )}
                                </div>
                                
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                    <small className="text-info">
                                        {conductoresExcluir.length} seleccionado(s)
                                    </small>
                                    {searchConductor && (
                                        <small className="text-muted">
                                            Mostrando {getConductoresFiltrados().length} de {cierreDetalle?.usuarios_moviles?.length || 0}
                                        </small>
                                    )}
                                </div>
                            </Col>

                            {/* Empresas a excluir */}
                            <Col md={4}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="small mb-0">Empresas a excluir</Form.Label>
                                    <div className="d-flex gap-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => {
                                                const allEmpresaIds = getEmpresasFiltradas().map(e => e.empresa_id);
                                                setEmpresasExcluir([...new Set([...empresasExcluir, ...allEmpresaIds])]);
                                            }}
                                            disabled={getEmpresasFiltradas().length === 0}
                                            title="Seleccionar todas las empresas visibles"
                                        >
                                            Todas
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setEmpresasExcluir([])}
                                            disabled={empresasExcluir.length === 0}
                                        >
                                            Ninguna
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Buscador de empresas */}
                                <div className="mb-2">
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar empresa..."
                                        value={searchEmpresa}
                                        onChange={(e) => setSearchEmpresa(e.target.value)}
                                        size="sm"
                                    />
                                </div>
                                
                                <div 
                                    className="border rounded p-2" 
                                    style={{ 
                                        maxHeight: '200px', 
                                        overflowY: 'auto',
                                        backgroundColor: '#f8f9fa'
                                    }}
                                >
                                    {getEmpresasFiltradas().length > 0 ? (
                                        getEmpresasFiltradas().map(empresa => (
                                            <Form.Check
                                                key={empresa.empresa_id}
                                                type="checkbox"
                                                id={`empresa-${empresa.empresa_id}`}
                                                label={empresa.sigla}
                                                checked={empresasExcluir.includes(empresa.empresa_id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setEmpresasExcluir([...empresasExcluir, empresa.empresa_id]);
                                                    } else {
                                                        setEmpresasExcluir(empresasExcluir.filter(id => id !== empresa.empresa_id));
                                                    }
                                                }}
                                                className="mb-1"
                                            />
                                        ))
                                    ) : (
                                        <small className="text-muted">
                                            {searchEmpresa ? 'No se encontraron empresas con ese término' : 'No hay empresas disponibles'}
                                        </small>
                                    )}
                                </div>
                                
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                    <small className="text-info">
                                        {empresasExcluir.length} seleccionada(s)
                                    </small>
                                    {searchEmpresa && (
                                        <small className="text-muted">
                                            Mostrando {getEmpresasFiltradas().length} de {cierreDetalle?.empresas?.length || 0}
                                        </small>
                                    )}
                                </div>
                            </Col>

                            {/* TMS a excluir */}
                            <Col md={4}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="small mb-0">TMS a excluir</Form.Label>
                                    <div className="d-flex gap-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => {
                                                const allTmsIds = getTmsFiltrados().map(t => t.id);
                                                setTmsExcluir([...new Set([...tmsExcluir, ...allTmsIds])]);
                                            }}
                                            disabled={getTmsFiltrados().length === 0}
                                            title="Seleccionar todos los TMS visibles"
                                        >
                                            Todos
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setTmsExcluir([])}
                                            disabled={tmsExcluir.length === 0}
                                        >
                                            Ninguno
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Buscador de TMS */}
                                <div className="mb-2">
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar TMS..."
                                        value={searchTms}
                                        onChange={(e) => setSearchTms(e.target.value)}
                                        size="sm"
                                    />
                                </div>
                                
                                <div 
                                    className="border rounded p-2" 
                                    style={{ 
                                        maxHeight: '200px', 
                                        overflowY: 'auto',
                                        backgroundColor: '#f8f9fa'
                                    }}
                                >
                                    {getTmsFiltrados().length > 0 ? (
                                        getTmsFiltrados().map(tms => (
                                            <Form.Check
                                                key={tms.id}
                                                type="checkbox"
                                                id={`tms-${tms.id}`}
                                                label={tms.tms}
                                                checked={tmsExcluir.includes(tms.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setTmsExcluir([...tmsExcluir, tms.id]);
                                                    } else {
                                                        setTmsExcluir(tmsExcluir.filter(id => id !== tms.id));
                                                    }
                                                }}
                                                className="mb-1"
                                            />
                                        ))
                                    ) : (
                                        <small className="text-muted">
                                            {searchTms ? 'No se encontraron TMS con ese término' : 'No hay TMS disponibles'}
                                        </small>
                                    )}
                                </div>
                                
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                    <small className="text-info">
                                        {tmsExcluir.length} seleccionado(s)
                                    </small>
                                    {searchTms && (
                                        <small className="text-muted">
                                            Mostrando {getTmsFiltrados().length} de {cierreDetalle?.tms?.length || 0}
                                        </small>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Resumen de exclusiones */}
                    {(conductoresExcluir.length > 0 || empresasExcluir.length > 0 || tmsExcluir.length > 0) && (
                        <Alert variant="info" className="mb-3">
                            <strong>Resumen de exclusiones:</strong>
                            {conductoresExcluir.length > 0 && (
                                <div>
                                    <small>
                                        Conductores excluidos: {getExclusionSummary().conductoresNames.join(', ')}
                                    </small>
                                </div>
                            )}
                            {empresasExcluir.length > 0 && (
                                <div>
                                    <small>Empresas excluidas: {getExclusionSummary().empresasNames.join(', ')}</small>
                                </div>
                            )}
                            {tmsExcluir.length > 0 && (
                                <div>
                                    <small>TMS excluidos: {getExclusionSummary().tmsNames.join(', ')}</small>
                                </div>
                            )}
                        </Alert>
                    )}
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={sendingEmails}>
                    Cancelar
                </Button>
                <Button
                    variant={envioTest ? 'warning' : 'success'}
                    onClick={handleSubmit}
                    disabled={loading || sendingEmails}>
                    {sendingEmails ? (
                        <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            Enviando...
                        </>
                    ) : (
                        <>
                            <FaIcons.FaEnvelope className="me-2" />
                            {envioTest ? 'Enviar Prueba' : 'Enviar Correos'}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

SendEmailModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSendEmails: PropTypes.func.isRequired,
    cierreDetalle: PropTypes.object,
    loading: PropTypes.bool,
    sendingEmails: PropTypes.bool,
    emailTemplates: PropTypes.array,
    loadingEmailTemplates: PropTypes.bool
}

SendEmailModal.defaultProps = {
    cierreDetalle: null,
    loading: false,
    sendingEmails: false,
    emailTemplates: [],
    loadingEmailTemplates: false
}

export default SendEmailModal
